import React from 'react';
import { Type, Check } from 'lucide-react';
import { useFontSize, FontSizeOption, FONT_SIZE_OPTIONS } from '../contexts/FontSizeContext';

interface FontSizeSelectorProps {
  className?: string;
  variant?: 'full' | 'compact' | 'segmented';
  theme?: 'light' | 'dark';
  showPreview?: boolean;
  label?: string;
}

export default function FontSizeSelector({ 
  className = '', 
  variant = 'full',
  theme = 'light',
  showPreview = true,
  label = 'Tamanho da Fonte (Acessibilidade)'
}: FontSizeSelectorProps) {
  const { fontSize, setFontSize, fontDetails, options } = useFontSize();
  const isDark = theme === 'dark';

  // Variante Compacta (Pills para Header/Barra de Ferramentas/Drawer)
  if (variant === 'compact' || variant === 'segmented') {
    return (
      <div className={`inline-flex items-center gap-1 p-1 rounded-xl ${
        isDark 
          ? 'bg-white/10 backdrop-blur-sm border border-white/15' 
          : 'bg-zinc-100 border border-zinc-200/80'
      } ${className}`}>
        {options.map((opt) => {
          const isActive = fontSize === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFontSize(opt.id)}
              className={`flex-1 px-2.5 py-1.5 rounded-lg text-xs font-black transition-all cursor-pointer flex items-center justify-center min-w-7 active:scale-95 ${
                isActive
                  ? 'bg-white text-zinc-900 shadow-xs border border-zinc-200/90'
                  : isDark
                  ? 'text-white/80 hover:text-white hover:bg-white/10'
                  : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-200/60'
              }`}
              title={`${opt.label} - ${opt.description}`}
              aria-label={opt.label}
              aria-pressed={isActive}
            >
              <span className={opt.id === 'small' ? 'text-[11px] font-black' : opt.id === 'large' ? 'text-sm font-black' : 'text-xs font-black'}>
                {opt.shortLabel}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  // Variante Completa (Configurações, Perfil, Onboarding)
  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
            <Type className="h-3.5 w-3.5 text-brand" />
            <span>{label}</span>
          </label>
          <p className="text-xs text-zinc-500 font-medium mt-0.5">
            Ajuste para uma leitura mais confortável sem quebrar a visualização.
          </p>
        </div>
        <span className="text-[10px] font-black text-brand bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100 uppercase tracking-wider">
          {fontDetails.label}
        </span>
      </div>

      {/* 3 Opções de Tamanho */}
      <div className="grid grid-cols-3 gap-2">
        {options.map((opt) => {
          const isActive = fontSize === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFontSize(opt.id)}
              className={`p-3 rounded-2xl border text-center transition-all cursor-pointer flex flex-col items-center justify-center gap-1 active:scale-95 ${
                isActive
                  ? 'bg-brand text-white border-brand shadow-sm shadow-brand/20'
                  : 'bg-zinc-50 hover:bg-emerald-50/40 border-zinc-100 hover:border-emerald-200 text-zinc-700'
              }`}
              aria-label={opt.label}
              aria-pressed={isActive}
            >
              <div className="flex items-center gap-1.5">
                <span className={`font-black leading-none ${
                  opt.id === 'small' ? 'text-xs' : opt.id === 'large' ? 'text-base' : 'text-sm'
                }`}>
                  {opt.shortLabel}
                </span>
                {isActive && <Check className="h-3 w-3 stroke-3" />}
              </div>
              <span className={`text-xs font-black ${isActive ? 'text-white' : 'text-zinc-900'}`}>
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Amostra Visual em Tempo Real */}
      {showPreview && (
        <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-100 space-y-1">
          <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Prévia da Leitura:</span>
          <p className="text-xs text-zinc-800 font-medium leading-relaxed">
            "Orçamento #1042 • Instalação Elétrica Residencial: R$ 480,00"
          </p>
        </div>
      )}
    </div>
  );
}
